import { Box, CardContent, Tab, Tabs, Theme, Tooltip } from '@mui/material';
import Card from '@mui/material/Card';
import { makeStyles } from 'tss-react/mui';
import React, { memo, useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../app/store';
import useAgentInterception from '../../../hooks/agentInterception/useAgentInterception.hooks';
import { cartActions } from '../../../reducers/cartSlice';
import { dialogActions } from '../../../reducers/dialogSlice';
import useItemConversion from '../../../hooks/useItemConversion.hooks';
import {
  AgentInterceptionType,
  messagingActions,
  TextFrameTransmissionMessage,
} from '../../../reducers/messagingSlice';
import {
  selectDialogSelectedModGroup,
  selectSelectedItemCartId,
} from '../../../selectors/dialog';
import {
  modSymbolMappingSelector,
  posSettingsSelector,
  selectIsExpressUpsellMode,
  selectIsExpressTimerActive,
} from '../../../selectors/menu';
import { selectUnavailableItemsByName } from '../../../selectors/menu';
import { selectFeatureFlagSuppressUpsell } from '../../../redux/features/config/config.selector';
import { SetCartItemRef } from '../../../types';
import { getItemUnavailableMessage } from '../../../utils/TTSMessage';
import { CartItem } from '../../../utils/cart';
import Colors from '../../../utils/color';
import { sortModGroups } from '../../../utils/menu';
import ModGroup from './ModGroup';
import { ParsedMenuItem, ParsedModifierGroup } from '../../../types/menu';
import useModifierSelectionInfo from '../../../hooks/useModifierSelectionInfo/useModifierSelectionInfo.hooks';
import { getCartItemDescription } from '../util/helper';
import { getMenuUnificationFeatureFlag } from '../../../utils/session-storage';
import { UPSELL, UPSELL_PREFIX } from '../../../constants';

const useStyles = makeStyles()((theme: Theme) => ({
  menuItemCard: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  // hitl_3_0 only: tighter vertical margins (8px instead of 16px).
  menuItemCardCompact: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  menuItemCardContent: {
    padding: '0px !important',
  },
  textFieldContainer: {
    '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
      border: '1px solid rgba(0, 0, 0, 0.12)',
    },
  },
  tabsContainer: {
    backgroundColor: Colors.gallery,
    borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
    marginBottom: theme.spacing(0.5),
    '& .MuiTab-root': {
      backgroundColor: Colors.porcelain,
    },
    '& .MuiButtonBase-root.MuiTab-root': {
      padding: `0 0 0 ${theme.spacing(1.25)}`,
      fontWeight: 'bold !important',
    },
    '& .MuiButtonBase-root.MuiTab-root.Mui-selected': {
      color: Colors.black,
      backgroundColor: Colors.white,
    },
  },
  menuItemTabPanel: {
    '& > div': {
      padding: '0 !important',
    },
  },
  tabPanelBox: {
    borderColor: 'rgba(0, 0, 0, 0.12)',
    padding: '0',
  },
  tabLabel: {
    color: '#78716c',
    backgroundColor: '#fffbeb',
    padding: '5px',
    fontWeight: 'normal',
    fontSize: 'small',
    textTransform: 'none',
    textAlign: 'left',
    wordBreak: 'break-word',
  },
  tabContent: {
    color: Colors.daintree,
    padding: '1rem',
    paddingBottom: '0rem',
    fontSize: '0.75rem',
    width: '100%',
    maxWidth: 'none',
  },
  convertedItemName: {
    backgroundColor: '#e8f5e8', // Light green background for converted items
    padding: '2px 6px',
    borderRadius: '4px',
    border: '1px solid #4caf50', // Green border
    fontWeight: 'bold',
  },
}));

const TabPanel = ({
  children,
  index,
  value,
}: {
  children: React.ReactNode;
  index: number;
  value: number;
}) => {
  const { classes } = useStyles();
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`menu-item-choice-panel-${index}`}
      aria-labelledby={`menu-item-choice-panel-${index}`}
    >
      {value === index && (
        <Box className={classes.tabPanelBox}>
          <div className={classes.menuItemTabPanel}>{children}</div>
        </Box>
      )}
    </div>
  );
};

interface CartItemDetailProps {
  cartItem: CartItem;
  setCartItemRef: SetCartItemRef;
}

const CartItemDetail = ({ cartItem, setCartItemRef }: CartItemDetailProps) => {
  const { classes, cx } = useStyles();
  const dispatch = useDispatch();
  const isHitl3 = useSelector(
    (state: RootState) => state.config.FEATURE_FLAG_HITL_3_0
  );
  const { handleModifierSelection } = useItemConversion();
  const [tabValue, setTabValue] = useState(0);
  const { cartItems } = useSelector((state: RootState) => state.cart);
  const modSymbolMapping = useSelector(modSymbolMappingSelector);
  const selectedModGroup = useSelector(selectDialogSelectedModGroup);
  const { selectedModGroupPath } = useSelector(
    (state: RootState) => state.dialog
  );
  const selectedItemCartId = useSelector(selectSelectedItemCartId);
  const posSettings = useSelector(posSettingsSelector);
  const unavailableItemsByName = useSelector(selectUnavailableItemsByName);
  const suppressUpsellEnabled = useSelector(selectFeatureFlagSuppressUpsell);
  const expressUpsellMode = useSelector(selectIsExpressUpsellMode);
  const expressTimerActive = useSelector(selectIsExpressTimerActive);
  const { sendAgentInterception } = useAgentInterception();
  const { sendModifierSelectionInfo } = useModifierSelectionInfo();
  const isUnifiedMenu = getMenuUnificationFeatureFlag();

  const sendAgentInterventionEvent = useCallback(
    (description?: string, type?: AgentInterceptionType) => {
      sendAgentInterception({
        data: {
          type: type || AgentInterceptionType.ORDER_CHANGED,
          description,
        },
      });
    },
    [sendAgentInterception]
  );

  const onTabChange = useCallback(
    (event: React.SyntheticEvent<Element, Event>, value: any) => {
      setTabValue(value);
    },
    []
  );

  const sendMessage = useCallback(
    (message: string) => {
      const payload: Partial<TextFrameTransmissionMessage> = {
        data: { payload: message, status: 'final' },
      };
      dispatch(messagingActions.sendMessage(payload as any));
    },
    [dispatch]
  );

  const descriptionToSend = getCartItemDescription(cartItem, isUnifiedMenu);
  const onTabClick = useCallback(
    (event: React.SyntheticEvent<Element, Event>) => {
      if (descriptionToSend) {
        sendMessage(descriptionToSend);
        sendAgentInterventionEvent(
          `${cartItem.name} description is sent as TTS`,
          AgentInterceptionType.TTS_SENT
        );
      }
    },
    [cartItem.description, sendMessage, sendAgentInterventionEvent]
  );

  const onMenuItemModifierSelect = useCallback(
    (
      menuItem: ParsedMenuItem | undefined,
      modGroup: ParsedModifierGroup,
      pathToItem: string,
      selected: boolean,
      fromClick?: boolean,
      isDefaultSelection?: boolean
    ) => {
      if (!menuItem) return;
      // Out-of-stock (86'd today) or meal-period inactive — both surface as
      // `available: false` in the parsed menu item.
      if (menuItem.available === false) {
        sendMessage(getItemUnavailableMessage(menuItem.name));
        return;
      }

      sendModifierSelectionInfo({ menuItem, modGroup, selected });
      if (
        menuItem.name.toLocaleLowerCase() !== 'declined' &&
        !(
          menuItem.name.toLocaleLowerCase() === 'decline' &&
          (cartItem.name.startsWith(UPSELL_PREFIX) ||
            cartItem.name.startsWith(UPSELL))
        )
      ) {
        dispatch(
          cartActions.selectModifier({
            cartItemId: cartItem.cartItemId,
            menuItemId: menuItem.itemId,
            modGroupId: modGroup.id,
            selected,
            modCode: '',
            modSymbolMapping,
            pathToItem,
            posSettings,
            readback: true,
            applySuppressCascade: true,
            suppressUpsellEnabled,
            applyExpressCascade: expressUpsellMode,
            applyStopAllCascade: expressTimerActive,
          })
        );

        if (fromClick)
          sendAgentInterventionEvent(`${menuItem.name} added to order`);
      } else {
        dispatch(cartActions.deleteCartItem(cartItem.cartItemId.toString()));
        if (fromClick)
          sendAgentInterventionEvent(`${cartItem.name} removed from order`);

        const cartItemIds = Object.keys(cartItems)
          .filter((id) => id !== cartItem.cartItemId.toString())
          .map((id) => parseInt(id));
        const newCartItemId = Math.max(...cartItemIds);
        if (newCartItemId > 0) {
          dispatch(
            dialogActions.selectItemAndResetNavigation({
              item: cartItems[newCartItemId.toString()],
              itemCartId: newCartItemId,
            })
          );
        } else {
          dispatch(dialogActions.selectItemAndResetNavigation(undefined));
        }
      }

      // Check for item conversion and option replacement AFTER processing the normal modifier selection
      // This ensures the agent intervention event is sent for the conversion size selection
      // Skip conversion logic for default selections to prevent duplicate processing
      if (!isDefaultSelection)
        handleModifierSelection(modGroup, menuItem.name, cartItem);
    },
    [
      cartItem,
      cartItems,
      dispatch,
      handleModifierSelection, // Added to dependencies
      modSymbolMapping,
      posSettings, // Added to dependencies
      sendAgentInterventionEvent,
      sendModifierSelectionInfo, // Added to dependencies
      sendMessage,
      suppressUpsellEnabled,
      expressUpsellMode,
    ]
  );

  const modifierGroups = sortModGroups(cartItem);

  return (
    <Card
      className={cx(
        classes.menuItemCard,
        isHitl3 && classes.menuItemCardCompact
      )}
      elevation={3}
      sx={{ borderRadius: 1.5 }}
    >
      <CardContent className={classes.menuItemCardContent}>
        <Tabs
          value={tabValue}
          onClick={onTabClick}
          onChange={onTabChange}
          aria-label={cartItem.name}
          TabIndicatorProps={{ style: { display: 'none' } }}
          className={classes.tabsContainer}
        >
          <Tab
            label={
              <div
                style={{ display: 'flex', gap: '10px', alignItems: 'center' }}
              >
                <div
                  style={{ flexShrink: 0, fontSize: '15px' }}
                  className={
                    cartItem.isConverted ? classes.convertedItemName : ''
                  }
                >
                  {cartItem.name}{' '}
                </div>
                <div className={classes.tabLabel}>
                  {descriptionToSend || ''}
                </div>
              </div>
            }
            ref={(ref) => {
              setCartItemRef(cartItem.cartItemId, ref);
            }}
            sx={{ alignItems: 'start' }}
            className={classes.tabContent}
          />
        </Tabs>
        <TabPanel value={tabValue} index={0}>
          {modifierGroups.map((modGroup: ParsedModifierGroup) => (
            <ModGroup
              key={`${modGroup.id}_display`}
              cartItem={cartItem}
              modGroup={modGroup}
              selectMenuItemModifier={onMenuItemModifierSelect}
              pathToItem={modGroup.id}
              isSelectedToFocus={
                selectedModGroupPath === modGroup.id &&
                selectedItemCartId === cartItem.cartItemId
              }
            />
          ))}
        </TabPanel>
      </CardContent>
    </Card>
  );
};

export default memo(CartItemDetail);
